import { injectIntl } from "react-intl";
import React, { useCallback, useEffect, useState } from "react";
import apiService from "src/services/api.service";
import { OperationType } from "src/helpers/Enums";
import SmsPricingTable from "./SmsPricingTable";
import AddPricing from "./AddPricing";
import DeletePricing from "./DeletePricing";

const formatCurrency = (value) => {
  if (value == null || value === "" || isNaN(value)) {
    return "$0.0000";
  }
  return `$${parseFloat(value).toFixed(4)}`;
};

const parsePricingResponse = (response) => {
  const data = response?.data || {};
  const active = data.active || {};
  const entries = Array.isArray(data.history) ? data.history : [];
  const costPerMessage =
    data.cost_per_message ?? active.cost_per_message ?? null;
  const totalPerMessage =
    active.total_per_message ??
    data.total_per_message ??
    costPerMessage;

  return {
    entries,
    summary: { totalPerMessage, costPerMessage },
  };
};

const SmsPricing = () => {
  const [pricingList, setPricingList] = useState([]);
  const [summary, setSummary] = useState({
    totalPerMessage: null,
    costPerMessage: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isAddPricingOpen, setIsAddPricingOpen] = useState(false);
  const [isDeletePricingOpen, setIsDeletePricingOpen] = useState(false);
  const [operationType, setOperationType] = useState();
  const [chosenPricing, setChosenPricing] = useState();

  const loadPricing = useCallback(() => {
    setIsLoading(true);
    apiService
      .getSmsPricing()
      .then((response) => {
        const { entries, summary: nextSummary } =
          parsePricingResponse(response);
        setPricingList(entries);
        setSummary(nextSummary);
      })
      .catch(() => {
        setPricingList([]);
        setSummary({ totalPerMessage: null, costPerMessage: null });
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const handleOpenAddPricing = () => {
    setOperationType(OperationType.CREATE);
    setChosenPricing(undefined);
    setIsAddPricingOpen(true);
  };

  const handleOpenEditPricing = (pricing) => {
    setOperationType(OperationType.UPDATE);
    setChosenPricing(pricing);
    setIsAddPricingOpen(true);
  };

  const handleOpenDeletePricing = (pricing) => {
    setChosenPricing(pricing);
    setIsDeletePricingOpen(true);
  };

  const closeModal = () => {
    setIsAddPricingOpen(false);
    setIsDeletePricingOpen(false);
    loadPricing();
  };

  return (
    <div className="SmsPricingContent">
      <div className="SmsPricingSummaryCard">
        <div className="SmsPricingSummaryMain">
          <div className="SmsPricingSummaryLabel">TOTAL PER MESSAGE</div>
          <div className="SmsPricingSummaryValue">
            {formatCurrency(summary.totalPerMessage)}
          </div>
        </div>
        <div className="SmsPricingSummarySide">
          <div className="SmsPricingSummaryLabel">COST/MESSAGE</div>
          <div className="SmsPricingSummarySideValue">
            {formatCurrency(summary.costPerMessage)}
          </div>
        </div>
      </div>

      <div className="PricingHistoryCard">
        <div className="PricingHistoryMain">
          <div className="PricingHistoryMainLeft">
            <div className="PricingHistoryMainTitle">Pricing History</div>
            <div className="PricingHistoryMainSubtitle">
              Historical pricing changes with effective dates for accurate cost
              reporting
            </div>
          </div>
          <button
            type="button"
            className="PrimaryButton"
            onClick={handleOpenAddPricing}
          >
            <img
              alt="Add"
              src="/media/icons/plusIcon.svg"
              className="w-4 h-4"
            />
            Add Pricing
          </button>
        </div>
        <div className="PricingHistorySide">
          <div className="PricingHistorySideLabel">Cost/Msg</div>
          <div className="PricingHistoryTableTitleDivider"></div>
          <div className="PricingHistorySideLabel">Created</div>
          <div className="PricingHistoryTableTitleDivider"></div>
          <div className="PricingHistorySideLabel">Notes</div>
        </div>
      </div>

      <div className="SmsPricingHistoryCard">
        {isLoading ? (
          <div className="PricingHistoryTableBody">Loading...</div>
        ) : (
          <SmsPricingTable
            list={pricingList}
            handleOpenEditPricing={handleOpenEditPricing}
            handleOpenDeletePricing={handleOpenDeletePricing}
          />
        )}
      </div>

      {isAddPricingOpen && (
        <AddPricing
          type={operationType}
          pricing={chosenPricing}
          close={closeModal}
        />
      )}
      {isDeletePricingOpen && (
        <DeletePricing pricing={chosenPricing} close={closeModal} />
      )}
    </div>
  );
};

export default injectIntl(SmsPricing);
